import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Search, Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getPositionsForSport } from '@/data/sportsPositions';
import { getUserTypeDisplayName, getUserTypeBadgeVariant } from '@/utils/userTypeDisplay';

interface User {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  user_type: 'supabase' | 'internal';
}

interface TeamConnection {
  id: string;
  user_id: string;
  position?: string;
  user?: User;
}

interface AddPlayerFormProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  teamName: string;
  teamSport?: string;
  onUpdate: () => void;
}

export const AddPlayerForm = ({
  isOpen,
  onClose,
  teamId,
  teamName,
  teamSport = 'Basketball',
  onUpdate
}: AddPlayerFormProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState<TeamConnection[]>([]);

  // Get sport-specific positions
  const getSportPositions = (sport?: string): string[] => {
    if (!sport) return ['Player'];
    return getPositionsForSport(sport);
  };

  // Load current team players
  const loadTeamPlayers = async () => {
    try {
      setLoading(true);
      
      const { data: teamPlayers, error: playersError } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', teamId);

      if (!playersError && teamPlayers) {
        const playersWithUsers = await Promise.all(
          teamPlayers.map(async (player) => {
            let userData = null;
            
            // Try internal users first for better under-13 user support
            const { data: internalUser } = await supabase
              .from('internal_users')
              .select('id, username, role')
              .eq('id', player.user_id)
              .maybeSingle();
            
            if (internalUser) {
              userData = { 
                id: internalUser.id,
                username: internalUser.username,
                first_name: '',
                last_name: '',
                role: internalUser.role,
                user_type: 'internal' as const 
              };
            } else {
              // Fallback to Supabase users
              const { data: profile } = await supabase
                .from('profiles')
                .select('id, username, first_name, last_name, role')
                .eq('id', player.user_id)
                .single();
              
              if (profile) {
                userData = { ...profile, user_type: 'supabase' as const };
              }
            }
            
            return {
              id: player.user_id + '-player',
              user_id: player.user_id,
              position: player.position,
              user: userData
            };
          })
        );
        setPlayers(playersWithUsers);
      }
    } catch (error) {
      console.error('Error loading team players:', error);
      toast.error('Failed to load team players');
    } finally {
      setLoading(false);
    }
  };

  // Search users
  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      setAvailableUsers([]);
      return;
    }

    try {
      setLoading(true);
      
      // Search Supabase users
      const { data: supabaseUsers, error: supabaseError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Search internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_user_profiles')
        .select('user_id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Combine results
      const combined: User[] = [
        ...(supabaseUsers || []).map(user => ({
          id: user.id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'supabase' as const
        })),
        ...(internalUsers || []).map(user => ({
          id: user.user_id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'internal' as const
        }))
      ];

      // Filter out existing players
      const existingPlayerIds = players.map(p => p.user_id);
      const filtered = combined.filter(user => !existingPlayerIds.includes(user.id));

      setAvailableUsers(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  // Add player
  const addPlayer = async (userId: string, userType: 'supabase' | 'internal') => {
    try {
      // Check if player is already on the team by checking database directly
      const { data: existingPlayer } = await supabase
        .from('team_players')
        .select('user_id')
        .eq('team_id', teamId)
        .eq('user_id', userId)
        .single();
      
      if (existingPlayer) {
        toast.error('This player is already on the team');
        return;
      }

      const { error } = await supabase
        .from('team_players')
        .insert({
          team_id: teamId,
          user_id: userId,
          position: selectedPosition
        });
      
      if (error) {
        console.error('Database error:', error);
        if (error.code === '23505') {
          toast.error('This player is already on the team');
        } else {
          toast.error(`Failed to add player: ${error.message}`);
        }
        return;
      }

      toast.success('Player added successfully');
      loadTeamPlayers();
      onUpdate();
      setSearchTerm('');
      setSelectedPosition('');
      setAvailableUsers([]);
    } catch (error) {
      console.error('Error adding player:', error);
      toast.error('Failed to add player');
    }
  };

  // Remove player
  const removePlayer = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('team_players')
        .delete()
        .eq('team_id', teamId)
        .eq('user_id', userId);
      
      if (error) {
        console.error('Database error:', error);
        toast.error(`Failed to remove player: ${error.message}`);
        return;
      }

      toast.success('Player removed successfully');
      loadTeamPlayers();
      onUpdate();
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error('Failed to remove player');
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadTeamPlayers();
    }
  }, [isOpen, teamId]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const getUserDisplayName = (user?: User) => {
    if (!user) return 'Unknown User';
    return user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            Manage Players - {teamName} {teamSport && `(${teamSport})`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add Player Section */}
          <Card>
            <CardHeader>
              <CardTitle>Add New Player</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="player-search">Search Players</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="player-search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by username or name..."
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="player-position">Position</Label>
                  <Select value={selectedPosition} onValueChange={setSelectedPosition}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      {getSportPositions(teamSport).map((position) => (
                        <SelectItem key={position} value={position}>
                          {position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Search Results */}
              {searchTerm && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                   {availableUsers.map((user) => {
                     const isAlreadyPlayer = players.some(p => p.user_id === user.id);
                     return (
                      <div key={user.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                        <div>
                          <span className="font-medium">{getUserDisplayName(user)}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {user.role}
                            </Badge>
                            <Badge variant={getUserTypeBadgeVariant(user.user_type)} className="text-xs">
                              {getUserTypeDisplayName(user.user_type)}
                            </Badge>
                          </div>
                          {isAlreadyPlayer && (
                            <span className="text-xs text-yellow-600 ml-2">(Already on team)</span>
                          )}
                        </div>
                        <Button
                          onClick={() => addPlayer(user.id, user.user_type)}
                          size="sm"
                          className="bg-sports-green text-black hover:bg-sports-green/90 font-bold"
                          disabled={isAlreadyPlayer}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {isAlreadyPlayer ? 'Added' : 'Add'}
                        </Button>
                      </div>
                    );
                  })}
                  {availableUsers.length === 0 && searchTerm && (
                    <p className="text-muted-foreground text-center py-4">No users found</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Players Section */}
          <Card>
            <CardHeader>
              <CardTitle>Current Players ({players.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {players.map((player) => (
                  <div key={player.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div>
                      <span className="font-medium">{getUserDisplayName(player.user)}</span>
                      {player.position && (
                        <span className="text-sm text-muted-foreground ml-2">- {player.position}</span>
                      )}
                    </div>
                    <Button
                      onClick={() => removePlayer(player.user_id)}
                      size="sm"
                      variant="destructive"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {players.length === 0 && (
                  <p className="text-muted-foreground text-center py-8">No players added yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};